/* External */
import {
  useCallback,
  memo,
  useEffect,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useHistory, useLocation } from 'react-router-dom';
import { withFocusable } from '@noriginmedia/react-spatial-navigation';
import { compose } from 'redux';

/* Ott-common */
import CardProfile from '@dtvgo/card-profile';

/* Theme Assets */
import ExpandedLogo from '~/assets/theme/svg/brand-logo.svg';

/* Components */
import { getJsonImage } from 'src/theme/images';
import Item from './Item';
import { withCursorVisibilityFilter } from '../../hocs/withCursorVisibilityFilter.hoc';

/* Providers */
import { useGtmLocalContext } from '../../providers/GtmLocalProvider';
import { useCountry } from '../../providers/ConfigProvider';
import { withFocusRestorable } from '../../providers/RestoreFocusProvider';

/* Images */
import sidebarBgGradient from '../../assets/images/sidebarBgGradient.png';

/* Utils */
import { Routes, getLink } from '../../utils/router';
import { SIDEBAR_ITEMS } from '../../utils/focusKey';
import { Direction } from '../../utils/spatialNavigation';
import { useOnBackPress } from '../../utils/keyDownEvents';
import platform from '../../utils/platform';
import {
  colors,
  isMyListEnabled,
  PageType,
  STORAGE_COUNTRY,
} from '../../utils/constants';
import {
  getParentLander,
  getTagEventFromRoute,
  GTM_CATEGORIES,
  GTM_EVENT_NAMES,
  GTM_SUBCATEGORIES,
} from '../../utils/googleTags';
import {
  backButtonWantToLeaveActive,
  WANT_TO_LEAVE_COUNT_BACK,
  isMyPageActive,
} from '../../utils/env';

/* Hooks */
import useClickCallback from '../../hooks/useClickCallback';
import useResetFocus from '../../hooks/useResetFocus';
import useRefSync from '../../hooks/useRefSync';
import { useCloseAppCompletely } from '../../hooks/useShouldCloseAppCompletely';

import WantToLeaveModal from '../WantToLeaveModal/WantToLeaveModal';

/* Styles */
import './Sidebar.scss';

function Sidebar({
  expanded,
  open,
  close,
  focused,
  hasFocusedChild,
  setFocus,
  isModalShown,
  className = '',
}) {
  const storedProfiles = useSelector((state) => state.profile.profiles)
    ?? JSON.parse(localStorage.getItem('profiles'));
  const userProfiles = storedProfiles ?? [];
  const selectedProfile = JSON.parse(localStorage.getItem('profile'));
  const profileIndex = userProfiles.findIndex(
    (userProfile) => userProfile?.id === selectedProfile?.id,
  );
  const { commonTaggingEvent } = useGtmLocalContext();
  const setAllSessionStorage = useResetFocus();
  const history = useHistory();
  const { pathname, state } = useLocation();
  const expandedRef = useRefSync(expanded);
  const country = useCountry();
  const SidebarBgImage = getJsonImage('sidebarBgGradient') || sidebarBgGradient;
  const [countBackButton, setCountBackButton] = useState(0);
  const maxBackButtonCountToCloseApp = WANT_TO_LEAVE_COUNT_BACK || 2;

  const styles = {
    background: `url(${SidebarBgImage}) repeat-y`,
    backgroundSize: 'contain',
    zIndex: 500,
  };

  const menuCommonTagEvent = useCallback(({ eventName }) => {
    const tagFromRoute = getTagEventFromRoute({ path: pathname });
    if (eventName) {
      commonTaggingEvent({
        category: GTM_CATEGORIES.MENU_DATA,
        subCategory: GTM_SUBCATEGORIES.MENU,
        eventName,
        data: {
          tab: getParentLander(tagFromRoute) || tagFromRoute,
          providerName: state?.providerName,
        },
      });
    }
  }, [commonTaggingEvent, pathname, state?.providerName]);

  const handleOnBackEvent = useCallback((direction) => {
    menuCommonTagEvent({ eventName: GTM_EVENT_NAMES.MENU.BACK });
    if (direction !== Direction.RIGHT && backButtonWantToLeaveActive) {
      setCountBackButton((prevState) => prevState + 1);
    }
    close();
  }, [close, menuCommonTagEvent, setCountBackButton]);

  const handleClick = useClickCallback(useCallback((event) => {
    open();
    event.stopPropagation();
  }, [open]));

  const handleClickOutside = useClickCallback(useCallback(() => {
    close();
  }, [close]));

  useOnBackPress({ listener: handleOnBackEvent, when: expanded });

  const handleEnterPress = useCallback(({ to, event }) => {
    if (expandedRef.current) {
      menuCommonTagEvent({ eventName: getTagEventFromRoute({ path: getLink(to) }) });
      setAllSessionStorage();
      history.push(getLink(to));
      close();
      if (event) {
        event.stopPropagation();
      }
    }
  }, [history, close, setAllSessionStorage, expandedRef, menuCommonTagEvent]);

  const [isNavigatingIntoSideBar, setIsNavigatingIntoSideBar] = useState(false);

  const shouldCloseAppCompletely = useCloseAppCompletely();
  const shouldDisplayMyListOrMyPage = isMyListEnabled || isMyPageActive;
  const myListOrMyPageKey = isMyPageActive ? 'myPage' : 'myList';

  const myListOrMyPageProps = {
    myPage: {
      focusKey: SIDEBAR_ITEMS.MY_PAGE,
      label: 'sidebar.item.myPage',
      route: Routes.Private.MyPage,
    },
    myList: {
      focusKey: SIDEBAR_ITEMS.MY_LIST,
      label: 'sidebar.item.myList',
      route: Routes.Private.MyList,
    },
  };

  const handleExit = useCallback(() => {
    menuCommonTagEvent({ eventName: GTM_EVENT_NAMES.MENU.LOG_OUT });
    if (shouldCloseAppCompletely) {
      platform.closeApp();
    } else {
      platform.sendAppToBackground();
    }
  }, [menuCommonTagEvent, shouldCloseAppCompletely]);

  const handleArrowPress = useCallback((direction) => {
    if (direction === Direction.RIGHT) {
      handleOnBackEvent(direction);
      return false;
    }

    setIsNavigatingIntoSideBar(direction === Direction.UP || direction === Direction.DOWN);

    return true;
  }, [handleOnBackEvent]);

  useEffect(() => {
    if (country) {
      localStorage.setItem(STORAGE_COUNTRY, country);
    }
  }, [country]);

  useEffect(() => {
    if (!focused && !hasFocusedChild) {
      setIsNavigatingIntoSideBar(false);
    }
  }, [focused, hasFocusedChild]);

  useEffect(() => {
    if (expanded && !isModalShown) {
      setFocus();
    }
  }, [expanded, setFocus, isModalShown]);

  // Want to Leave Modal condition to Show
  if (countBackButton >= maxBackButtonCountToCloseApp && backButtonWantToLeaveActive) {
    return (<WantToLeaveModal setCountBackButton={setCountBackButton} handleExit={handleExit} />);
  }

  return (
    <div
      className={`dtv-html5-c-sidebar ${expanded ? 'dtv-html5-c-sidebar--expanded' : ''} ${className}`}
      onMouseDown={handleClickOutside}
      aria-hidden="true"
      style={platform.osName === 'WhaleOS' ? styles : {}}
    >
      <nav
        className="dtv-html5-c-sidebar__nav"
        onMouseDown={handleClick}
        aria-hidden="true"
      >
        <img
          src={getJsonImage('brand-logo') || ExpandedLogo}
          alt="dtv-logo"
          className="dtv-html5-c-sidebar__image dtv-html5-c-sidebar__image--expanded"
        />

        <ul className="dtv-html5-c-sidebar__content">
          <Item
            focusKey={SIDEBAR_ITEMS.PROFILE}
            label={`${selectedProfile?.name}`}
            pageId={PageType.PROFILE}
            to={Routes.Private.Profiles}
            sidebarFocused={focused || hasFocusedChild}
            focusRestorable={false}
            onEnterPress={handleEnterPress}
            onArrowPress={handleArrowPress}
            isNavigatingIntoSideBar={isNavigatingIntoSideBar}
          >
            <div>
              <CardProfile
                name={selectedProfile?.name}
                color={selectedProfile?.color || colors[profileIndex + 1]}
                className="dtv-html5-c-card-profile--logo"
              />
            </div>
          </Item>
          <Item
            focusKey={SIDEBAR_ITEMS.HOME}
            label="sidebar.item.home"
            icon="home"
            pageId={PageType.HOME}
            to={Routes.Private.Home}
            sidebarFocused={focused || hasFocusedChild}
            focusRestorable={false}
            onEnterPress={handleEnterPress}
            onArrowPress={handleArrowPress}
            isNavigatingIntoSideBar={isNavigatingIntoSideBar}
          />
          <Item
            focusKey={SIDEBAR_ITEMS.LIVE}
            label="sidebar.item.live"
            icon="live"
            pageId={PageType.LIVE}
            to={Routes.Private.Live}
            sidebarFocused={focused || hasFocusedChild}
            focusRestorable={false}
            onEnterPress={handleEnterPress}
            onArrowPress={handleArrowPress}
            isNavigatingIntoSideBar={isNavigatingIntoSideBar}
          />
          <Item
            focusKey={SIDEBAR_ITEMS.ON_DEMAND}
            label="sidebar.item.watch"
            icon="watch"
            pageId={PageType.VOD}
            to={Routes.Private.OnDemand}
            sidebarFocused={focused || hasFocusedChild}
            focusRestorable={false}
            onEnterPress={handleEnterPress}
            onArrowPress={handleArrowPress}
            isNavigatingIntoSideBar={isNavigatingIntoSideBar}
          />
          <Item
            focusKey={SIDEBAR_ITEMS.SPORTS}
            label="sidebar.item.sports"
            icon="trophy"
            pageId={PageType.SPORTS}
            to={Routes.Private.Sports}
            sidebarFocused={focused || hasFocusedChild}
            focusRestorable={false}
            onEnterPress={handleEnterPress}
            onArrowPress={handleArrowPress}
            isNavigatingIntoSideBar={isNavigatingIntoSideBar}
          />
          <Item
            focusKey={SIDEBAR_ITEMS.KIDS}
            label="sidebar.item.kids"
            icon="kids"
            pageId={PageType.KIDS}
            to={Routes.Private.Kids}
            sidebarFocused={focused || hasFocusedChild}
            focusRestorable={false}
            onEnterPress={handleEnterPress}
            onArrowPress={handleArrowPress}
            isNavigatingIntoSideBar={isNavigatingIntoSideBar}
          />
          <Item
            focusKey={SIDEBAR_ITEMS.SEARCH}
            label="sidebar.item.search"
            icon="search"
            to={Routes.Private.Search}
            pageId={PageType.SEARCH}
            sidebarFocused={focused || hasFocusedChild}
            focusRestorable={false}
            onEnterPress={handleEnterPress}
            onArrowPress={handleArrowPress}
            isNavigatingIntoSideBar={isNavigatingIntoSideBar}
          />
          {shouldDisplayMyListOrMyPage && (
            <Item
              focusKey={myListOrMyPageProps[myListOrMyPageKey].focusKey}
              label={myListOrMyPageProps[myListOrMyPageKey].label}
              icon="bookmark"
              to={myListOrMyPageProps[myListOrMyPageKey].route}
              sidebarFocused={focused || hasFocusedChild}
              focusRestorable={false}
              onEnterPress={handleEnterPress}
              onArrowPress={handleArrowPress}
              isNavigatingIntoSideBar={isNavigatingIntoSideBar}
            />
          )}
        </ul>
        <ul className="dtv-html5-c-sidebar__configuration">
          <Item
            focusKey={SIDEBAR_ITEMS.SETTING}
            label="sidebar.item.setting"
            icon="settings"
            to={Routes.Private.Setting}
            sidebarFocused={focused || hasFocusedChild}
            focusRestorable={false}
            onEnterPress={handleEnterPress}
            onArrowPress={handleArrowPress}
            isNavigatingIntoSideBar={isNavigatingIntoSideBar}
          />
          <Item
            focusKey={SIDEBAR_ITEMS.EXIT}
            label="common.exitLabel"
            icon="exit"
            sidebarFocused={focused || hasFocusedChild}
            focusRestorable={false}
            onEnterPress={handleExit}
            onArrowPress={handleArrowPress}
            isNavigatingIntoSideBar={isNavigatingIntoSideBar}
          />
        </ul>
      </nav>
    </div>
  );
}

Sidebar.propTypes = {
  className: PropTypes.string,
  expanded: PropTypes.bool.isRequired,
  open: PropTypes.func.isRequired,
  close: PropTypes.func.isRequired,
  focused: PropTypes.bool.isRequired,
  hasFocusedChild: PropTypes.bool.isRequired,
  setFocus: PropTypes.func.isRequired,
  isModalShown: PropTypes.bool.isRequired,
};

const enhance = compose(
  withFocusable({ trackChildren: true }),
  withCursorVisibilityFilter,
  withFocusRestorable,
);

export default memo(enhance(memo(Sidebar)));
